import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { ReactNode, Suspense, lazy, memo, useEffect } from 'react'
import OAuthCallback from '@/pages/oauth/Callback'
import Navbar from '@/components/Navbar'
import { redirectToCheckout } from '@/services/stripe'
import { PRICING_PLANS } from '@/types/pricing'
import { usePageTracking } from '@/hooks/usePageTracking'
import { PageTitle } from '@/components/PageTitle'

const Landing = lazy(() => import('@/pages/Home'))
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const Settings = lazy(() => import('@/pages/Settings'))
const Sheets = lazy(() => import('@/pages/Sheets'))
const ApiDocs = lazy(() => import('@/pages/ApiDocs'))
const McpDocs = lazy(() => import('@/pages/McpDocs'))
const Success = lazy(() => import('@/pages/Success'))
const Cancel = lazy(() => import('@/pages/Cancel'))

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
  </div>
)

const RequireSubscription = memo(({ children }: { children: ReactNode }) => {
  const { user, loading, subscriptionLoading, hasActiveSubscription } = useAuth()

  useEffect(() => {
    if (!loading && !subscriptionLoading && hasActiveSubscription === false && user) {
      // Find the real-time-sync plan
      const realTimeSyncPlan = PRICING_PLANS.find((plan) => plan.id === 'real-time-sync')
      if (realTimeSyncPlan) {
        // Redirect to Stripe checkout instead of paywall
        redirectToCheckout(realTimeSyncPlan.priceId)
      }
    }
  }, [loading, subscriptionLoading, hasActiveSubscription, user])

  if (loading || subscriptionLoading || hasActiveSubscription === null) return <LoadingSpinner />
  if (!user) return <Navigate to="/login" />
  if (!hasActiveSubscription) return <LoadingSpinner /> // Show loading while redirecting to checkout
  return <>{children}</>
})

const HomeRedirect = memo(() => {
  const { user, loading, subscriptionLoading, hasActiveSubscription } = useAuth()

  useEffect(() => {
    if (!loading && !subscriptionLoading && hasActiveSubscription === false && user) {
      // Find the real-time-sync plan
      const realTimeSyncPlan = PRICING_PLANS.find((plan) => plan.id === 'real-time-sync')
      if (realTimeSyncPlan) {
        // Redirect to Stripe checkout instead of paywall
        redirectToCheckout(realTimeSyncPlan.priceId)
      }
    }
  }, [loading, subscriptionLoading, hasActiveSubscription, user])

  if (loading || subscriptionLoading || hasActiveSubscription === null) return <LoadingSpinner />
  if (!user) return <Landing />
  if (!hasActiveSubscription) return <LoadingSpinner /> // Show loading while redirecting to checkout
  return hasActiveSubscription ? <Navigate to="/dashboard" /> : <LoadingSpinner />
})

const AuthenticatedLayout = memo(({ children }: { children: ReactNode }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-16">{children}</div>
    </div>
  )
})

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  )
}

function AppContent() {
  usePageTracking()

  return (
    <>
      <PageTitle />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={<HomeRedirect />} />

          <Route
            path="/dashboard"
            element={
              <RequireSubscription>
                <AuthenticatedLayout>
                  <Dashboard />
                </AuthenticatedLayout>
              </RequireSubscription>
            }
          />

          <Route
            path="/settings"
            element={
              <RequireSubscription>
                <AuthenticatedLayout>
                  <Settings />
                </AuthenticatedLayout>
              </RequireSubscription>
            }
          />

          <Route
            path="/sheets"
            element={
              <RequireSubscription>
                <AuthenticatedLayout>
                  <Sheets />
                </AuthenticatedLayout>
              </RequireSubscription>
            }
          />

          <Route
            path="/api-docs"
            element={
              <RequireSubscription>
                <AuthenticatedLayout>
                  <ApiDocs />
                </AuthenticatedLayout>
              </RequireSubscription>
            }
          />

          <Route
            path="/mcp-docs"
            element={
              <RequireSubscription>
                <AuthenticatedLayout>
                  <McpDocs />
                </AuthenticatedLayout>
              </RequireSubscription>
            }
          />

          <Route path="/oauth/callback" element={<OAuthCallback />} />
          <Route path="/success" element={<Success />} />
          <Route path="/cancel" element={<Cancel />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    </>
  )
}

export default App
